import { Box, Divider, Paper, Stack, Typography } from "@mui/material";

import { dirTokens } from "../tokens";
import EmptyState from "./EmptyState";

/**
 * SectionCard — outlined Paper section used across Directory detail
 * pages (Identity / Accounts / Groups / Roles on UserDetail,
 * Entitlements / Assigned-to on RoleDetail, Members / Roles on the
 * Group drawer).
 *
 * Standardises:
 *   - title + optional count
 *   - optional action button on the right of the header
 *   - Divider between header and body
 *   - Automatic EmptyState when the section is empty
 *
 * Props:
 *   title            : section title
 *   count            : optional number appended in parentheses
 *   action           : right-side action node (usually a Button)
 *   isEmpty          : boolean — when true, `emptyState` renders in the body
 *   emptyState       : { title, description, primaryAction }
 *                      or a full <EmptyState /> node
 *   children         : the section body (rendered when !isEmpty)
 */
export default function SectionCard({
  title,
  count,
  action,
  isEmpty,
  emptyState,
  children,
  sx,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: dirTokens.radius, ...sx }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, minHeight: 32 }}
      >
        <Typography variant="subtitle2">
          {title}
          {typeof count === "number" && (
            <Box
              component="span"
              sx={{ color: "text.secondary", ml: 0.75, fontWeight: 400 }}
            >
              ({count})
            </Box>
          )}
        </Typography>
        {action}
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      {isEmpty ? (
        emptyState && emptyState.$$typeof ? (
          emptyState
        ) : (
          <EmptyState
            compact
            title={emptyState?.title}
            description={emptyState?.description}
            primaryAction={emptyState?.primaryAction}
          />
        )
      ) : (
        children
      )}
    </Paper>
  );
}
